import React, { ChangeEvent, useState, useEffect } from "react";
import Modal from "ui/Modal";
import Checkbox from "ui/Checkbox";
import Typography from "ui/Typography";
import styled from "styled-components/macro";
import ConsentManager from "utils/ConsentManager";
import { SurfaceType } from "utils/analytics/analyticsConstants";
import { theme } from "ui/theme";

const $Table = styled.table`
  font-size: ${theme.FontSizeTooltip};
  margin-top: ${theme.SizeXl};
  border: 1px solid ${theme.ColorSneaker100};
  th {
    border: 1px solid ${theme.ColorSneaker100};
    background-color: ${theme.ColorSneaker50};
    font-weight: ${theme.FontWeightNormal};
    padding: ${theme.SizeMd};
  }
  td {
    border: 1px solid ${theme.ColorSneaker100};
    vertical-align: top;
    padding: ${theme.SizeMd};
    &:first-child {
      text-align: center;
    }
  }
`;

const $Checkbox = styled(Checkbox)`
  margin-top: -0.2rem;
`;

export type CookieSettingsModalProps = {
  show: boolean;
  onClose: () => void;
};

export default function CookieSettingsModal(props: CookieSettingsModalProps) {
  const [marketingCookies, setMarketingCookies] = useState(false);

  useEffect(() => {
    const integrations = ConsentManager.integrations();
    if (!integrations) {
      throw new Error("Default tracking preferences are missing.");
    }
    // GTM is reponsible for loading the rest of our tracking services
    setMarketingCookies(integrations["Google Tag Manager"]);
  }, []);

  const closeButtonProps = {
    onClick: props.onClose,
    analyticsName: "clicked_save",
    analyticsSurface: SurfaceType.COOKIE_SETTINGS,
  };

  const onSave = () => {
    ConsentManager.setIntegrations(
      {
        "Google Tag Manager": marketingCookies,
      },
      /* userPreferences = */ true,
    );
    props.onClose();
  };

  return (
    <Modal
      show={props.show}
      size="lg"
      close={closeButtonProps}
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>Data Collection Preferences</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Typography size="bodySansSerif">
          We use data collected by cookies and JavaScript libraries to improve
          your browsing experience, analyze site traffic, deliver personalized
          advertisements, and increase the overall performance of our site.
        </Typography>
        <$Table>
          <thead>
            <tr>
              <th>
                <Typography color="ColorSpace500" size="tooltip">
                  Allow
                </Typography>
              </th>
              <th>Category</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <$Checkbox
                  aria-label="Marketing cookies. On selection these cookies are allowed"
                  checked={marketingCookies}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setMarketingCookies(event.target.checked)
                  }
                />
              </td>
              <td>Marketing and Social Media Cookies</td>
              <td>
                We use certain cookies to inform our own marketing and digital
                content efforts, which help us tell people about the work we do.
                For example, if a Twitter or LinkedIn user visits a Gradient
                Learning website, Twitter or LinkedIn can then identify them as
                part of a group of Gradient Learning website visitors in order
                to serve them with marketing and digital content messages about
                Gradient Learning and our work. You can go to these websites via
                the links below to opt-out of these marketing and digital
                content messages. There are no third-party ads on any Gradient
                Learning websites, so we don’t use any cookies to serve
                third-party ads.
              </td>
            </tr>
            <tr>
              <td>
                <$Checkbox
                  checked
                  disabled
                  aria-label="Essential cookies disabled"
                />
              </td>
              <td>Strictly Necessary</td>
              <td>
                Certain cookies are strictly necessary for the website to
                function and can’t be switched off in our systems. You can set
                your browser to block or alert you about these cookies, but some
                parts of the site won’t work as a result.
              </td>
            </tr>
          </tbody>
        </$Table>
      </Modal.Body>
      <Modal.Footer>
        <Modal.PrimaryButton
          block={false}
          onClick={onSave}
          analyticsName="clicked_save_cookie_settings"
        >
          Save
        </Modal.PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
}
